import React from 'react'
import { AppProvider, useApp } from './context/AppContext'
import { deletesOn } from './privacy/closing'
import { pendingAgreements } from './privacy/agreements'
import Agreements from './screens/Agreements'
import { ApiError, api } from './api/client'
import { screenCache } from './api/screenCache'
import { goalsDraft } from './plan/goalsDraft'
import { earningsDraft } from './screens/earnings/earningsDraft'
import { accountChanged, restartApp } from './auth/restart'
import { forgetOpenPanel, forgetWhereAt, restoreWhereAt } from './context/whereAt'
import Auth from './screens/Auth'
import AuthLayout, { AuthLogo } from './screens/AuthLayout'
import Logo from './ui/Logo'
import { Icon, IconButton } from './ui/Icon'
import Paid from './screens/Paid'
import Confirming, { CONFIRMING_PATH } from './screens/Confirming'
import StripeContinue from './screens/StripeContinue'
import { noteBackFromStripe, stripeReturnFrom } from './payments/stripeOnboarding'
import Today from './screens/Today'
import Calendar from './screens/Calendar'
import Clients from './screens/Clients'
import ClientProfile from './screens/ClientProfile'
import Earnings from './screens/Earnings'
import Goals from './screens/Goals'
import TabBar from './components/TabBar'
import Menu from './components/Menu'
import AppointmentSheet from './components/AppointmentSheet'
import BookingSheet from './components/BookingSheet'
import TapChoiceSheet from './components/TapChoiceSheet'
import TimeBlockSheet from './components/TimeBlockSheet'
import Toast from './components/Toast'
import Bell from './notifications/Bell'
import { UPDATE_READY } from './pwa/updates'
import SessionExpired from './components/SessionExpired'
import ErrorBoundary, { AppError, SectionError, isChunkLoadError } from './components/ErrorBoundary'
import LazySection, { retryableLazy } from './components/LazySection'
import type { Retryable } from './components/LazySection'
import type { SettingsPanel } from './types/app'
import BusinessPanel from './screens/settings/BusinessPanel'
import ServicesPanel from './screens/settings/ServicesPanel'
import PackagesPanel from './screens/settings/PackagesPanel'
import OffersPanel from './screens/settings/OffersPanel'
import FormsPanel from './screens/settings/FormsPanel'
import HoursPanel from './screens/settings/HoursPanel'
import PlanPanel from './screens/settings/PlanPanel'
import Panel from './screens/settings/Panel'
import SettingsGroup, { groupOf, sectionId } from './screens/settings/SettingsGroup'
import './App.css'
import './screens/Auth.css'

// Lazy: the panel carries the embedded Connect components, needed only here.
// Each can be loaded again from its section's Retry if the download fails.
const ONLINE_BOOKING = retryableLazy(() => import('./screens/settings/OnlineBookingPanel'))
const REVIEWS = retryableLazy(() => import('./screens/settings/ReviewsPanel'))
const CARD_PAYMENTS = retryableLazy(() => import('./screens/settings/CardPaymentsPanel'))
const CALENDAR_FEED = retryableLazy(() => import('./screens/settings/CalendarFeedPanel'))
const PRIVACY = retryableLazy(() => import('./screens/settings/PrivacyPanel'))

// Each older Settings panel, shown as a section of its group's page: a lazy
// one by where to load it from, the others as they are.
const SETTINGS_SECTIONS: Record<Exclude<SettingsPanel, 'privacy'>, Retryable | React.ReactNode> = {
  plan: <PlanPanel />,
  business: <BusinessPanel />,
  hours: <HoursPanel />,
  services: <ServicesPanel />,
  packages: <PackagesPanel />,
  offers: <OffersPanel />,
  forms: <FormsPanel />,
  'online-booking': ONLINE_BOOKING,
  reviews: REVIEWS,
  'card-payments': CARD_PAYMENTS,
  'calendar-feed': CALENDAR_FEED,
}

const isRetryable = (value: unknown): value is Retryable =>
  typeof value === 'object' && value !== null && 'reset' in value && 'get' in value

// reloadFresh reloads on Today rather than on the screen that just failed.
function reloadFresh() {
  forgetWhereAt()
  window.location.reload()
}

function PrivacySettings() {
  const [attempt, setAttempt] = React.useState(0)
  const forgotten = React.useRef<string | null>(null)
  const Privacy = PRIVACY.get()
  const failed = (onRetry: () => void) => (
    <Panel title="Privacy & records">
      <SectionError onRetry={onRetry} />
    </Panel>
  )
  // As LazySection: a failed download is retried by reloading, back here.
  return (
    <ErrorBoundary
      resetKey={attempt}
      onError={() => { forgotten.current = forgetOpenPanel() ?? forgotten.current }}
      fallback={(_retry, error) =>
        failed(() => {
          if (isChunkLoadError(error)) {
            restoreWhereAt(forgotten.current)
            window.location.reload()
            return
          }
          PRIVACY.reset()
          setAttempt((n) => n + 1)
        })
      }
    >
      <React.Suspense fallback={null}>
        <Privacy />
      </React.Suspense>
    </ErrorBoundary>
  )
}

function AppContent() {
  const { state, dispatch } = useApp()
  const mainRef = React.useRef<HTMLElement>(null)
  const closingOn = deletesOn(state.me)
  const agreementsDue = pendingAgreements(state.me)
  // Back from Stripe Checkout: the confirming page, until she leaves it for
  // her plan in Settings.
  const [confirming, setConfirming] = React.useState(() => CONFIRMING_PATH.test(window.location.pathname))
  // Back from Stripe's onboarding form ('back'), or sent on for a fresh link
  // to it ('continue'). The return is noted here, before anything renders:
  // the Card payments card may already be open (whereAt reopens it after
  // the reload), and it reads the note as it opens.
  const [stripeReturn, setStripeReturn] = React.useState(() => {
    const r = stripeReturnFrom(window.location.pathname)
    if (r === 'back') noteBackFromStripe()
    return r
  })

  // Each tab is its own screen — always start at the top of it.
  React.useEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0 })
  }, [state.activeTab])

  const checkSession = React.useCallback(async () => {
    dispatch({ type: 'SESSION_CHECKING' })
    try {
      dispatch({ type: 'SESSION_STARTED', payload: await api.me() })
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        dispatch({ type: 'SESSION_ENDED' })
      } else {
        dispatch({ type: 'SESSION_UNREACHABLE' })
      }
    }
  }, [dispatch])

  React.useEffect(() => {
    checkSession()
  }, [checkSession])

  // Any 401 while the app is open means the session has ended: one "signed
  // out" state over the app, instead of every save failing on its own.
  React.useEffect(() => {
    api.onUnauthorized(() => dispatch({ type: 'SESSION_EXPIRED' }))
    return () => api.onUnauthorized(null)
  }, [dispatch])

  // Drafts and cached screens are per-account and live only in memory: clear
  // them whenever a session ends (an explicit sign-out or an expired one),
  // and when someone else signs in over an expired session, so they never
  // leak into whoever signs in next on this device. Other modules keep
  // per-account state too (the card account, the Stripe Connect session), so
  // once the account this page was opened for has gone, the page restarts.
  const professionalId = state.me?.professional.id ?? null
  const lastProfessional = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (accountChanged(lastProfessional.current, state.authStatus, professionalId)) {
      lastProfessional.current = null
      restartApp()
    }
    if (state.authStatus === 'signedOut') {
      goalsDraft.clearAll()
      earningsDraft.clearMonth()
      screenCache.clearAll()
    }
    if (professionalId && lastProfessional.current && lastProfessional.current !== professionalId) {
      goalsDraft.clearAll()
      earningsDraft.clearMonth()
      screenCache.clearAll()
    }
    if (professionalId) lastProfessional.current = professionalId
  }, [state.authStatus, professionalId])

  // A new build has taken over (src/pwa/updates.ts). The screen still runs
  // the old code until it reloads, so offer that rather than reload under
  // her: she may be halfway through a form.
  React.useEffect(() => {
    const offer = () =>
      dispatch({
        type: 'SHOW_TOAST',
        payload: { message: 'A new version of Harmovi is ready.', action: { label: 'Reload', run: () => window.location.reload() } },
      })
    window.addEventListener(UPDATE_READY, offer)
    return () => window.removeEventListener(UPDATE_READY, offer)
  }, [dispatch])

  // Opening a login link while already signed in: drop the link from the address bar.
  React.useEffect(() => {
    if (state.authStatus === 'signedIn' && window.location.pathname === '/login') {
      window.history.replaceState(null, '', '/')
    }
  }, [state.authStatus])

  // Back from Stripe's form, she lands on her Card payments card, which checks
  // with Stripe and shows where she is.
  React.useEffect(() => {
    if (state.authStatus !== 'signedIn' || stripeReturn !== 'back') return
    window.history.replaceState(null, '', '/')
    setStripeReturn(null)
    dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'card-payments' })
  }, [state.authStatus, stripeReturn, dispatch])

  if (state.authStatus === 'loading') {
    // The same centred logo index.html shows before the code arrives.
    return (
      <div className="app-boot" aria-busy="true">
        <Logo height={28} />
      </div>
    )
  }

  if (state.authStatus === 'offline') {
    return (
      <AuthLayout>
        <div className="auth-form">
          <AuthLogo />
          <h1>Can’t reach Harmovi</h1>
          <p className="auth-subtitle">Check your connection, then try again.</p>
          <button type="button" className="btn btn-primary" onClick={checkSession}>
            Try again
          </button>
        </div>
      </AuthLayout>
    )
  }

  if (state.authStatus === 'signedOut') {
    return <Auth />
  }

  if (agreementsDue.length > 0) {
    return <Agreements pending={agreementsDue} />
  }

  if (confirming) {
    return (
      <Confirming
        onDone={() => {
          window.history.replaceState(null, '', '/')
          setConfirming(false)
          dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'plan' })
        }}
      />
    )
  }

  if (stripeReturn === 'continue') {
    return (
      <StripeContinue
        onDone={() => {
          window.history.replaceState(null, '', '/')
          setStripeReturn(null)
          dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'card-payments' })
        }}
      />
    )
  }

  const sheetKey = `${state.appointmentId}|${state.booking ? 'book' : ''}|${state.tapChoice ? 'choice' : ''}|${state.timeBlock ? 'block' : ''}|${state.settingsPanel}|${state.menuOpen}`
  const closeEverything = () => {
    dispatch({ type: 'CLOSE_SHEET' })
    dispatch({ type: 'CLOSE_SETTINGS_PANEL' })
    dispatch({ type: 'CLOSE_MENU' })
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <Logo className="app-header__brand" height={22} />
        <div className="app-header__actions">
          <Bell />
          <IconButton
            icon="menu"
            variant="filled"
            className="app-header__menu"
            label="Open settings"
            onClick={() => dispatch({ type: 'TOGGLE_MENU' })}
          />
        </div>
      </header>

      <main className="main-content scroll-container" ref={mainRef}>
        {closingOn && (
          <button
            type="button"
            className="closing-banner"
            onClick={() => dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'privacy' })}
          >
            Your account is closing. Your data is deleted on {closingOn}. <span>Download it or keep your account <Icon name="chevron-right" size={14} /></span>
          </button>
        )}
        <ErrorBoundary
          resetKey={`${state.activeTab}|${state.openClientId}`}
          fallback={() => <AppError onReload={reloadFresh} />}
        >
          {state.activeTab === 'today' && <Today />}
          {state.activeTab === 'calendar' && <Calendar />}
          {state.activeTab === 'clients' &&
            (state.openClientId ? <ClientProfile key={state.openClientId} /> : <Clients />)}
          {state.activeTab === 'earnings' && <Earnings />}
          {state.activeTab === 'goals' && <Goals />}
        </ErrorBoundary>
      </main>

      {/* Present on every screen from 1024px (the side rail's button); on a
          phone Goals keeps its save buttons at the bottom instead. */}
      <button
        type="button"
        className={`fab${state.activeTab === 'goals' ? ' fab--rail-only' : ''}`}
        aria-label="New booking"
        onClick={() => dispatch({ type: 'OPEN_BOOK_SHEET' })}
      >
        <Icon name="plus" size={24} />
        <span className="fab__label" aria-hidden="true">New booking</span>
      </button>

      <TabBar />

      <Toast />

      {/* Modals and sheets */}
      <ErrorBoundary
        resetKey={sheetKey}
        fallback={() => (
          <div className="sheet-overlay" onClick={closeEverything}>
            <div className="sheet-content sheet-error" role="dialog" aria-modal="true" aria-label="Something went wrong" onClick={(e) => e.stopPropagation()}>
              <AppError onReload={reloadFresh} />
              <button type="button" className="btn btn-quiet btn-block" onClick={closeEverything}>
                Close
              </button>
            </div>
          </div>
        )}
      >
        {state.menuOpen && <Menu />}
        {state.settingsPanel && state.settingsPanel !== 'privacy' && (
          <SettingsGroup panel={state.settingsPanel}>
            {groupOf(state.settingsPanel).panels.map((p) => {
              if (p === 'privacy') return null
              const section = SETTINGS_SECTIONS[p]
              return (
                <div key={p} id={sectionId(p)}>
                  {isRetryable(section) ? <LazySection source={section} /> : <LazySection>{section}</LazySection>}
                </div>
              )
            })}
          </SettingsGroup>
        )}
        {state.settingsPanel === 'privacy' && <PrivacySettings />}
        {state.appointmentId && <AppointmentSheet key={state.appointmentId} />}
        {state.booking && <BookingSheet />}
        {state.tapChoice && <TapChoiceSheet />}
        {state.timeBlock && <TimeBlockSheet />}
      </ErrorBoundary>

      {state.authStatus === 'expired' && <SessionExpired />}
    </div>
  )
}

export default function App() {
  // /paid is where a payer lands after paying by card. They have no session,
  // so it renders before (and without) the session check or any API call.
  if (/^\/paid\/?$/.test(window.location.pathname)) {
    return <Paid />
  }
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  )
}
